import { Injectable } from '@angular/core';

import { finalize, map, Observable } from 'rxjs';

import { AdditionalInfoItem, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and additional information items for an event
 */
@Injectable()
export class AdditionalInfoItemService {
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all additional information items for the specified event
     */
    public getIndex(
        eventId: string,
        includes: string[] = null,
        params: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<AdditionalInfoItem[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, params);

        return this.http.getPaged(AdditionalInfoItem, apiRequestUrl, forceRefresh)
            .pipe(map((pagedResponse: PagedResponse) => pagedResponse.recordList));
    }

    /**
     * Add or update the additionalInfoItem
     */
    public save(
        additionalInfoItem: AdditionalInfoItem,
        displaySpinner: boolean = false,
    ): Observable<AdditionalInfoItem> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(additionalInfoItem.eventId, additionalInfoItem.id);

        if (additionalInfoItem.id) {
            // Update existing AdditionalInfoItem
            response = this.http.patch(apiRequestUrl, additionalInfoItem.serialize(), displaySpinner);
        } else {
            // Add a new AdditionalInfoItem
            response = this.http.post(apiRequestUrl, additionalInfoItem.serialize(), displaySpinner);
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new AdditionalInfoItem())
                    .deserialize(response, response.included)),
                finalize(() => this.http.clearCache(AdditionalInfoItem)),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        additionalInfoItemId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/additional-info-items/`;

        if (additionalInfoItemId) {
            url += additionalInfoItemId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
